// ===========================================
// Parent Profile Hooks
// ===========================================
// React Query hooks for parent self-service functionality

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getMyFamily,
  getMyChildren,
  getMyChildEnrollments,
  getMyProfile,
  updateMyProfile,
  updateChildAvatar,
  updateChildContact,
  updateChildBirthDate,
  updateStudentNotes,
  UpdateProfileRequest,
  UpdateStudentContactInput,
} from '../api/user.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const parentProfileKeys = {
  all: ['parentProfile'] as const,
  family: () => [...parentProfileKeys.all, 'family'] as const,
  children: () => [...parentProfileKeys.all, 'children'] as const,
  childEnrollments: (studentId: string) => [...parentProfileKeys.all, 'childEnrollments', studentId] as const,
  profile: () => [...parentProfileKeys.all, 'profile'] as const,
};

// ===========================================
// QUERIES
// ===========================================

/**
 * Hook to fetch rich enrollments for one of the parent's children
 */
export function useMyChildEnrollments(studentId: string) {
  return useQuery({
    queryKey: parentProfileKeys.childEnrollments(studentId),
    queryFn: async () => {
      const response = await getMyChildEnrollments(studentId);
      return response.data;
    },
    enabled: !!studentId,
    staleTime: 60000,
  });
}

/**
 * Hook to fetch parent's family information
 */
export function useMyFamily() {
  return useQuery({
    queryKey: parentProfileKeys.family(),
    queryFn: async () => {
      const response = await getMyFamily();
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
}

/**
 * Hook to fetch parent's children (students)
 */
export function useMyChildren(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: parentProfileKeys.children(),
    queryFn: async () => {
      const response = await getMyChildren();
      return response.data;
    },
    staleTime: 60000, // 1 minute
    enabled: options?.enabled,
  });
}

/**
 * Hook to fetch parent's own profile
 */
export function useMyProfile() {
  return useQuery({
    queryKey: parentProfileKeys.profile(),
    queryFn: async () => {
      const response = await getMyProfile();
      return response.data;
    },
    staleTime: 60000, // 1 minute
  });
}

// ===========================================
// MUTATIONS
// ===========================================

/**
 * Hook to update parent's own profile
 */
export function useUpdateMyProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateProfileRequest) => updateMyProfile(data),
    onSuccess: () => {
      // Invalidate profile and family queries (family has parent info)
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.profile() });
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.family() });
    },
  });
}

/**
 * Hook to update child's avatar (for parents)
 */
export function useUpdateChildAvatar() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, avatar }: { studentId: string; avatar: string | null }) =>
      updateChildAvatar(studentId, avatar),
    onSuccess: () => {
      // Invalidate children query to refresh avatar data
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.children() });
    },
  });
}

/**
 * Hook to update child's contact details (for parents)
 */
export function useUpdateChildContact() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, data }: { studentId: string; data: UpdateStudentContactInput }) =>
      updateChildContact(studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.children() });
    },
  });
}

/**
 * Hook to set a child's date of birth (for parents)
 */
export function useUpdateChildBirthDate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, birthDate }: { studentId: string; birthDate: string }) =>
      updateChildBirthDate(studentId, birthDate),
    onSuccess: () => {
      // The age group shown across the parent area derives from the date.
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.all });
    },
  });
}

/**
 * Hook to update additional info (notes) for a student
 */
export function useUpdateStudentNotes() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ studentId, notes }: { studentId: string; notes: string | null }) =>
      updateStudentNotes(studentId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: parentProfileKeys.children() });
    },
  });
}
